/**
 * @file Single-line top chrome of the terminal/workspace pane —
 *       connection status + env label on the left; compact
 *       reset / clear / (optional) pause icon-buttons on the right.
 * @module @labcraft/lab/ui/components/terminal-header
 *
 * The connection dot pulses green when the underlying WebSocket is
 * `connected` and shows red otherwise. Reset-to-checkpoint plays a
 * 600 ms "RESETTING…" intermediate state and then resolves; actual
 * checkpoint restore is post-MVP (the design spec calls this out).
 *
 * #936 collapsed the previous label-bearing buttons into a single-line
 * row of icon-buttons (design §4.3) — same callbacks, same semantics
 * (`onClear`, `onReset`, optional `onPause` + `pauseDisabled`); only the
 * visual density changed. Accessible labels move to `aria-label` +
 * `title` since the glyphs no longer carry text.
 */

import { useState, type JSX } from 'react'
import type { SubscriptionStatus } from '@labcraft/core/frontend'
import { ClearXIcon, PauseIcon, ResetArrowIcon } from '@labcraft/ui'
import styles from './terminal_header.module.css'

/**
 * Props for {@link TerminalHeader}.
 *
 * @property status - Current WS subscription status; drives the
 *   connection pill color + label.
 * @property envLabel - Short env/machine label shown beside the
 *   connection status (e.g. "Nix · shell").
 * @property onClear - Clears the visible terminal buffer.
 * @property onReset - Resets to the chapter checkpoint (600 ms
 *   animation + no-op restore for MVP).
 * @property onPause - When provided, renders a Pause icon-button that
 *   calls the handler. Disabled while `pauseDisabled` is true (e.g. the
 *   VM isn't `running`, so suspending makes no sense). Absent `onPause`
 *   hides the button entirely so views that don't want the affordance
 *   (auth, splash) don't render it.
 * @property pauseDisabled - Disables the Pause button when true.
 */
export type TerminalHeaderProps = {
  status: SubscriptionStatus
  envLabel: string
  onClear: () => void
  onReset: () => void
  onPause?: () => void
  pauseDisabled?: boolean
}

/**
 * Single-line terminal-pane header.
 *
 * @param props - {@link TerminalHeaderProps}.
 * @returns The header row element.
 */
export function TerminalHeader(props: TerminalHeaderProps): JSX.Element {
  const [resetting, setResetting] = useState(false)
  const connected = props.status === 'connected'

  const handleReset = async (): Promise<void> => {
    if (resetting) return
    setResetting(true)
    try {
      props.onReset()
      await new Promise(r => setTimeout(r, 600))
    } finally {
      setResetting(false)
    }
  }

  return (
    <div className={styles.header}>
      <div className={styles.left}>
        <span
          className={`${styles.conn} ${connected ? styles.connOk : styles.connOff}`}>
          <span className={styles.dot} aria-hidden />
          <span>{connected ? 'Connected' : 'Disconnected'}</span>
        </span>
        <span className={styles.divider} aria-hidden />
        <span className={styles.env}>{props.envLabel}</span>
      </div>
      <div className={styles.right}>
        {props.onPause && (
          <button
            className={styles.iconBtn}
            type="button"
            onClick={props.onPause}
            disabled={props.pauseDisabled ?? false}
            title="Pause lab"
            aria-label="Pause lab — snapshot the VM and free host RAM">
            <PauseIcon />
          </button>
        )}
        <button
          className={styles.iconBtn}
          type="button"
          onClick={props.onClear}
          title="Clear terminal"
          aria-label="Clear terminal">
          <ClearXIcon />
        </button>
        <button
          className={`${styles.iconBtn} ${resetting ? styles.iconBtnBusy : ''}`}
          type="button"
          onClick={handleReset}
          disabled={resetting}
          title={resetting ? 'Resetting…' : 'Reset to checkpoint'}
          aria-label="Reset to checkpoint">
          <ResetArrowIcon />
        </button>
      </div>
    </div>
  )
}
